/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Overview tab (C4).
 *
 * Reads /api/network/dashboard which returns { overview: { networkSize,
 * topCompanies, topPositions, ... } }. Renders four visuals:
 *   - Stat cards (network size, unique companies, unique positions)
 *   - Top companies bar chart (from getTopCompanies output)
 *   - Top positions bar chart (from getTopPositions output)
 *   - Domain concentration pie (derived client-side from top positions
 *     via inferSkillsFromPosition — presentational only)
 *
 * recharts is already in package.json; no new dependency.
 */

import React from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const DOMAIN_COLORS = ['#00BCD4', '#0891b2', '#0e7490', '#155e75', '#164e63', '#94a3b8'];

// Client-side domain classification for the pie chart. Kept
// deliberately loose to avoid re-inventing the server's
// inferSkillsFromPosition — we just need buckets big enough to show
// on a pie.
function classifyDomain(position) {
  const p = (position || '').toLowerCase();
  if (/wms|warehouse|manhattan|blue yonder|sap ewm|3pl|logistics|supply chain/.test(p)) return 'WMS / Logistics';
  if (/rpg|as\/?400|iseries|ibm i/.test(p)) return 'IBM i / RPG';
  if (/civil|construction|boq|quantity surveyor/.test(p)) return 'Civil Construction';
  if (/erp|sap|erpnext/.test(p)) return 'ERP / Business Systems';
  if (/founder|chief|ceo|cto|cfo|cio|coo|president|vp\b|svp|evp|director|head of/.test(p)) return 'Leadership';
  if (/engineer|developer|programmer|architect|data|analyst/.test(p)) return 'Tech';
  return 'Other';
}

export default function NetworkOverview({ overview }) {
  if (!overview) {
    return <div style={emptyStyle}>Network data not available yet.</div>;
  }

  const {
    networkSize = 0,
    companiesInNetwork = 0,
    topCompanies = [],
    topPositions = []
  } = overview;

  // Domain buckets from the TOP positions we already have (not all
  // 15K — that would need a separate endpoint. This is presentational.)
  const domainCounts = new Map();
  for (const { position, count } of topPositions) {
    const d = classifyDomain(position);
    domainCounts.set(d, (domainCounts.get(d) || 0) + count);
  }
  const domainData = Array.from(domainCounts.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  const companyData = topCompanies.slice(0, 10).map(c => ({
    name: c.name,
    connections: c.connections
  }));

  const positionData = topPositions.slice(0, 10).map(p => ({
    name: p.position,
    count: p.count
  }));

  return (
    <div>
      {/* Stat cards */}
      <div style={cardRowStyle}>
        <StatCard label="Connections" value={networkSize.toLocaleString()} />
        <StatCard label="Unique companies" value={companiesInNetwork.toLocaleString()} />
        <StatCard label="Top position groups" value={topPositions.length} />
      </div>

      {/* Top companies */}
      <ChartCard title="Top companies in your network">
        {companyData.length === 0 ? (
          <div style={emptyStyle}>No company data.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={companyData} layout="vertical" margin={{ left: 60 }}>
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="connections" fill="#00BCD4" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Top positions */}
      <ChartCard title="Top roles in your network">
        {positionData.length === 0 ? (
          <div style={emptyStyle}>No position data.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={positionData} layout="vertical" margin={{ left: 60 }}>
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#0891b2" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      {/* Domain concentration */}
      <ChartCard title="Domain concentration">
        {domainData.length === 0 ? (
          <div style={emptyStyle}>Import network to see domain breakdown.</div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={domainData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={90}
                label={(entry) => `${entry.name} (${entry.value})`}
              >
                {domainData.map((_, idx) => (
                  <Cell key={idx} fill={DOMAIN_COLORS[idx % DOMAIN_COLORS.length]} />
                ))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </ChartCard>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={statCardStyle}>
      <div style={{ fontSize: '28px', fontWeight: 700, color: '#0f172a' }}>{value}</div>
      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{label}</div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div style={chartCardStyle}>
      <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{title}</h3>
      {children}
    </div>
  );
}

const cardRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: '12px',
  marginBottom: '20px'
};

const statCardStyle = {
  background: '#f8fafc',
  padding: '20px',
  borderRadius: '8px',
  textAlign: 'center'
};

const chartCardStyle = {
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '16px',
  marginBottom: '20px'
};

const emptyStyle = {
  padding: '40px 20px',
  textAlign: 'center',
  color: '#94a3b8',
  fontSize: '14px'
};
